/**
 * WHAT THIS APPLICATION IS CALLED, IN ONE PLACE.
 *
 * ⚠️ "Flexi" IS NOT THE PRODUCT'S NAME. The application is **Flexi Domains**;
 * Flexi Network is the company that publishes it. Copy that says "Flexi will
 * assume this role" names neither, and it leaves a customer unable to tell
 * which application is asking.
 *
 * That distinction stops being cosmetic the moment a second application asks
 * for something. A customer's AWS account will hold a role for Flexi Domains,
 * another for Flexi Email, and another for the testing platform. "Flexi needs
 * permission to delete your hosted zones" is then ambiguous in a way that
 * matters, because the answer might reasonably be yes for one product and no
 * for another.
 *
 * ⚠️ IMPORT THESE RATHER THAN TYPING THE NAME. A literal in a component is a
 * copy that drifts: renaming the product then means finding every string in
 * every module, and the ones that are missed are the customer-facing ones
 * nobody reads in review. There were 31 such literals before this file
 * existed.
 *
 * ⚠️ THE DOCUMENTATION SITE IS A SEPARATE ARTIFACT and cannot import this.
 * `flexi-domains-docs-app` declares `name="Flexi Domains"` in its Writerside
 * tree, and its published pages carry the same prose. A rename has to change
 * both, and the docs half is tracked separately.
 */

/**
 * The application, as a customer should see it named.
 *
 * Use this wherever copy refers to what the product does on a customer's
 * behalf: "PRODUCT_NAME will assume this role", "PRODUCT_NAME cannot reach
 * this provider".
 */
export const PRODUCT_NAME = 'Flexi Domains';

/**
 * The company that publishes the application.
 *
 * ⚠️ USE THIS ONLY WHERE THE COMPANY IS GENUINELY THE SUBJECT — a legal
 * notice, a support address, an IAM role name shared across products. For
 * anything a customer reads about what this application does, the subject is
 * the application, so use `PRODUCT_NAME`.
 */
export const COMPANY_NAME = 'Flexi Network';

/**
 * The prefix a customer's integration roles carry, for example
 * `FlexiNetwork-Domains-IntegrationRole`.
 *
 * ⚠️ THE COMPANY, NOT THE PRODUCT, AND DELIBERATELY SO. This clusters every
 * Flexi integration role together in a customer account holding thousands of
 * roles, so the product name is the SECOND segment rather than the first.
 * `FlexiNetwork-Domains-…` and `FlexiNetwork-Email-…` sort together;
 * `FlexiDomains-…` and `FlexiEmail-…` would not.
 *
 * ⚠️ It is an EXAMPLE shown to customers, never a requirement. A customer
 * names roles in their own account by their own conventions, and FNP-1361 is
 * what happens when the platform quietly requires a name it never asked for.
 */
export const INTEGRATION_ROLE_EXAMPLE =
    'arn:aws:iam::123456789012:role/FlexiNetwork-Domains-IntegrationRole';
